// eslint-disable-next-line @altermaind/you-shall-not-pass
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components';
import { Form } from '@/_shared/components/Form';
import { RenderLocalesWithComponents } from '@/_shared/components/RenderLocalesWithComponents';
import { getDocLink } from '@/_shared/constants/linksDoc';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useNotification } from '@/_shared/hooks/useNotification';
import { usePolling, UsePollingTaskParams } from '@/_shared/hooks/usePolling';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { useWizard, WizardNavigateCallback } from '@/_shared/hooks/useWizard';
import { FileTypes } from '@/_shared/types';
import { WIZARD_STEPS } from '@/configuration/constants/wizard';
import {
  getDownloadEmployeesImportsTemplateCall,
  postEmployeesImportsTemplateCall,
  getEmployeesImportsByJobIdCall,
  getEmployeesImportsStatsCall,
} from '@/people/state/api';
import { JobStatus } from '@/swagger/constants/anagraphics-enums';
import { LoaderFullPage } from '../LoaderFullPage';

type ImportFileResponse = ReturnType<(typeof getEmployeesImportsByJobIdCall)['hook']>['response'];

export const SetData = () => {
  const t = useTranslations('configuration.hr.setData');
  const tShared = useTranslations();
  const {
    loading: loadingGetDownloadEmployeesImportsTemplate,
    execute: executeGetDownloadEmployeesImportsTemplate,
  } = getDownloadEmployeesImportsTemplateCall.hook();
  const {
    jobId,
    success: successPostEmployeesImportsTemplate,
    loading: loadingPostEmployeesImportsTemplate,
    execute: executePostEmployeesImportsTemplate,
    error: errorPostEmployeesImportsTemplate,
  } = postEmployeesImportsTemplateCall.hook();
  const {
    isCompleted,
    inError,
    execute: executeGetEmployeesImportsByJobId,
  } = getEmployeesImportsByJobIdCall.hook();
  const { execute: executeGetEmployeesImportsStats, loading: loadingGetEmployeesImportsStats } =
    getEmployeesImportsStatsCall.hook();
  const { openDialog: openContacts } = useDialog('contacts');

  const { notify } = useNotification();
  const {
    setCustomOnNext,
    setCustomOnSkip,
    setNavigationButtonStatus,
    resetWizardNavigationCallbacks,
    resetWizardNavigationButtonStatus,
  } = useWizard();
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`);
  const gapXXL = convertSizeToNumber(`${useThemeValue('sizes', 'xxl')}`);
  const { genericResponsiveValue } = useResponsiveStyle();
  const { control, watch, handleSubmit, trigger } = useForm();
  const fieldValues = watch();

  const someInputFilled = useMemo(() => {
    return Object.keys(fieldValues).some((key: string) => !!fieldValues[key]);
  }, [fieldValues]);

  const fileDropped = useMemo(() => {
    return !!fieldValues?.companyDocumentInput;
  }, [fieldValues]);

  const handleDownload = useCallback(() => {
    executeGetDownloadEmployeesImportsTemplate({
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
  }, [executeGetDownloadEmployeesImportsTemplate, notify, tShared]);

  const pollJob = useCallback(
    ({
      args,
      onResult,
    }: UsePollingTaskParams<
      ImportFileResponse,
      { navigate?: () => void | undefined; jobId: string }
    >) => {
      if (!args?.navigate || !args?.jobId) {
        return;
      }

      executeGetEmployeesImportsByJobId({
        jobId: args.jobId,
        successSaga: ({ response }) => {
          onResult(response);
          if (response.status === JobStatus.COMPLETED) {
            executeGetEmployeesImportsStats({
              jobId: args.jobId,
              successSaga: () => {
                args?.navigate?.();
              },
              errorSaga: () => {
                notify(tShared('errors.genericApiError'), 'error');
              },
            });
          }
          if (response.status === JobStatus.FAILED) {
            notify(tShared('errors.genericApiError'), 'error');
          }
        },
        errorSaga: () => {
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
    },
    [executeGetEmployeesImportsByJobId, executeGetEmployeesImportsStats, notify, tShared],
  );

  const { start: startPolling } = usePolling<
    ImportFileResponse,
    { navigate?: () => void | undefined; jobId: string }
  >(pollJob, {
    intervalMs: 1500,
    backoff: 'exponential',
    maxIntervalMs: 5_000,
    retryOnError: true,
    stopWhen: res => res?.status === JobStatus.COMPLETED || res?.status === JobStatus.FAILED,
  });

  const onSubmit = useCallback(
    (navigate?: WizardNavigateCallback) => {
      executePostEmployeesImportsTemplate({
        file: fieldValues?.companyDocumentInput?.file,
        successSaga: ({ response }) => {
          startPolling({ navigate, jobId: response?.jobId || '' });
        },
        errorSaga: () => {
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executePostEmployeesImportsTemplate, fieldValues?.companyDocumentInput?.file],
  );

  const onSkip: NonNullable<Parameters<typeof setCustomOnSkip>[0]> = useCallback(navigate => {
    navigate?.(WIZARD_STEPS.CONFIGURATION_COMPLETED_THANK_YOU_PAGE);
  }, []);

  const showLoaderFileAnagraphicsUnderAnalysis = useMemo(() => {
    return (
      (jobId && successPostEmployeesImportsTemplate && !(isCompleted || inError)) ||
      loadingGetEmployeesImportsStats
    );
  }, [
    jobId,
    successPostEmployeesImportsTemplate,
    isCompleted,
    inError,
    loadingGetEmployeesImportsStats,
  ]);

  const goToDocs = useCallback(() => {
    window.open(getDocLink('UPLOAD_ANAGRAPHICS'), '_blank');
  }, []);

  const goToContact = useCallback(() => {
    openContacts({});
  }, [openContacts]);

  useEffect(() => {
    setCustomOnNext(navigate => onSubmit(navigate));
    setCustomOnSkip(onSkip);

    return () => {
      // Cleanup function to reset the custom onNext callback
      resetWizardNavigationCallbacks();
    };
  }, [
    handleSubmit,
    onSkip,
    onSubmit,
    resetWizardNavigationCallbacks,
    setCustomOnNext,
    setCustomOnSkip,
  ]);

  useEffect(() => {
    // Update the navigation button status based on form validity
    setNavigationButtonStatus({
      disableNextButton: !someInputFilled,
      isLoadingNextButton: loadingPostEmployeesImportsTemplate,
    });

    return () => {
      // Cleanup function to reset the navigation button status
      resetWizardNavigationButtonStatus();
    };
  }, [
    loadingPostEmployeesImportsTemplate,
    resetWizardNavigationButtonStatus,
    setNavigationButtonStatus,
    someInputFilled,
  ]);

  return (
    <Box mb='s'>
      <Box
        display='flex'
        flex={10}
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        style={{ gap: gapXXL }}
      >
        <Box
          width='100%'
          display='flex'
          flexDirection='column'
          justifyContent='left'
          alignItems='left'
          textAlign='left'
          flex={genericResponsiveValue({
            breakPoints: { desktop: 6 },
            defaultValue: 10,
          })}
        >
          <Box mb='m'>
            <Typography
              variant='h1'
              color='primary/text'
              fontSize={genericResponsiveValue({
                breakPoints: { desktop: 50 as never },
                defaultValue: 'xxxl',
              })}
              lineHeight={genericResponsiveValue({
                breakPoints: { desktop: '60px' as never },
                defaultValue: 'xxxl',
              })}
              fontWeight='bold'
              mb={genericResponsiveValue({
                breakPoints: { desktop: 'm' },
                defaultValue: 'xs',
              })}
            >
              {t('title')}
            </Typography>
            <Typography variant='h4' fontWeight={200} mt='s' color='primary/text'>
              {t('subtitle')}
            </Typography>
            <Box my='s'>
              <AlertBadge
                type='primary'
                iconName='info'
                label={t('warning')}
                labelProps={{
                  variant: 'p3/light',
                }}
              />
            </Box>
            <Typography variant='h4' fontWeight={200} mt='s' color='primary/text'>
              <RenderLocalesWithComponents
                text={t('downloadFile', {
                  downloadFile: '!{DOWNLOAD_FILE}!',
                })}
                placeholders={{
                  '!{DOWNLOAD_FILE}!': (
                    <Typography
                      as='span'
                      fontWeight='500'
                      style={{ textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }}
                      onClick={
                        loadingGetDownloadEmployeesImportsTemplate ? undefined : handleDownload
                      }
                    >
                      {t('downloadLabel')}
                    </Typography>
                  ),
                }}
              />
            </Typography>
          </Box>
        </Box>
        <Box
          display='flex'
          flex={genericResponsiveValue({
            breakPoints: { desktop: 4 },
            defaultValue: 10,
          })}
          style={{ gap: gapXS }}
          flexDirection='column'
          justifyContent='space-between'
          flexWrap='wrap'
          width='100%'
        >
          <Box width='100%'>
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='space-between'
              mb='xs'
            >
              <Form.FormFileInput
                control={control}
                trigger={trigger}
                label={t('uploadedFiles')}
                name='companyDocumentInput'
                MAX_SIZE_IN_MB={10}
                supportedFileTypes={[
                  FileTypes.EXCEL,
                  FileTypes.EXCEL_XLSX,
                  FileTypes.CSV,
                  FileTypes.WORD_DOCX,
                  FileTypes.PDF,
                ]}
                hasError={!!errorPostEmployeesImportsTemplate || !!inError}
              />
            </Box>
          </Box>
          {fileDropped ? (
            <AlertBadge type='transparent' iconName='info' label={t('warningSaveBeforeQuit')} />
          ) : (
            <AlertBadge
              type='transparent'
              iconName='info'
              label={
                <Typography variant='p2/light'>
                  <RenderLocalesWithComponents
                    text={t('infoBox', {
                      downloadFile: '!{DOCS}!',
                    })}
                    placeholders={{
                      '!{DOCS}!': (
                        <Typography
                          as='span'
                          fontWeight='500'
                          style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            color: 'inherit',
                          }}
                          onClick={goToDocs}
                        >
                          {t('labelDocs')}
                        </Typography>
                      ),
                      '!{CONTACT}!': (
                        <Typography
                          as='span'
                          fontWeight='500'
                          style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            color: 'inherit',
                          }}
                          onClick={goToContact}
                        >
                          {t('labelContact')}
                        </Typography>
                      ),
                    }}
                  />
                </Typography>
              }
            />
          )}
        </Box>
      </Box>
      {showLoaderFileAnagraphicsUnderAnalysis && (
        <LoaderFullPage title={t('loadingFile.title')} description={t('loadingFile.description')} />
      )}
    </Box>
  );
};
